import { useContext } from "react";
import cx from "classnames";
import _ from "lodash";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Collapse from "src/pages/asset/components/animation/collapse";
import StageActions from "../stageActions";
import Text from "src/components/text";

import { DealRoomActionPlanContext } from "../context";
import { StageStatus } from "src/types/dealRoom";
import { useDealRoomAdoption } from 'src/redux/actions/adoption/dealRoom';
import { DealRoomContext } from 'src/pages/dealRoom/components/context';

import styles from "./styles.module.scss";

const StatusBadge = ({ status }) => {
  const icon =
    status === StageStatus.Completed
      ? "paperflite-completed-action"
      : status === StageStatus.NotStarted
        ? "paperflite-not-started-actions"
        : "paperflite-pending-actions";

  return (
    <div
      className={cx(styles.badge, {
        [styles.completed]: status === StageStatus.Completed,
        [styles.progressing]: status === StageStatus.Progressing,
        [styles.notStarted]: status === StageStatus.NotStarted,
      })}
    >
      <i className={cx(styles.badgeIcon, icon)} />
      <p className={styles.statusText}>{status}</p>
    </div>
  );
};

const Stage = ({ stage, editing, creating, isDragOverlay }) => {
  const { trackCreateMapCreated } = useDealRoomAdoption();
  const { dealRoom } = useContext(DealRoomContext); 
  const {
    state,
    handleTitleChange,
    handleEditStage,
    handleToggleEditStage,
    handleCreateNewStage,
    handleUpdateStage,
    handleCancelCreateNewStage,
    handleCancelEditStage,
    clickDeleteStage,
    handleStageAccordionToggle,
  } = useContext(DealRoomActionPlanContext);

  const { id, title, completedActions, totalActions, isStageAccordionOpen } = stage;

  const editMode = _.get(state, "editMode");

  const editingItem = _.get(state, "editingItem");
  const shouldWiggle = _.get(editingItem, "type") === 'stage' && _.get(editingItem, "id") === id;
  const wiggleKey = _.get(editingItem, "wiggleKey", 0);

  const isEditStageSaving = _.get(state, "editingStage.loading", false);
  const isCreateStageSaving = _.get(state, "creatingStage.loading", false);
  const isStageDeleting = _.get(state, "deletingStage.loading", false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: stage.id,
    disabled: !editMode || !!editingItem,
    data: {
      stage: stage,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const stageStatus =
    completedActions === 0
      ? StageStatus.NotStarted
      : completedActions === totalActions
        ? StageStatus.Completed
        : StageStatus.Progressing;

  const isCompleted =
    totalActions === 0 ? false : completedActions === totalActions;

  const onChange = (newValue: string) => {
    if (creating) {
      handleTitleChange(newValue);
    } else if (editing) {
      handleEditStage(id, newValue);
    }
  };
  const onUpdateStage = () => {
    if (isEditStageSaving || isCreateStageSaving || _.size(title.trim()) === 0) {
      return;
    }

    if (creating) {
      handleCreateNewStage();
      trackCreateMapCreated({ 
        dealRoomName: _.get(dealRoom, "name", ""), 
        stageName: title
      });
    } else if (editing) {
      handleUpdateStage(id);
    }
  };

  const onCancelUpdateStage = () => {
    if (isEditStageSaving || isCreateStageSaving) {
      return;
    }
    
    if (creating) {
      handleCancelCreateNewStage();
    } else if (editing) {
      handleCancelEditStage();
    }
  };

  const handleAccordionClick = () => {
    if (!editMode && totalActions === 0) {
      return;
    }

    handleStageAccordionToggle(_.get(stage, "id"));
  }

  const handleTitleClick = () => {
    if (editMode) {
      handleToggleEditStage(_.get(stage, "id"))
      return;
    }

    handleAccordionClick();
  }

  return (
    <div
      className={cx(styles.stage, {
        [styles.disabled]: isDragOverlay,
      })}
      style={style}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
    >
      <div 
        className={cx(styles.header, {
          [styles.wiggle]: shouldWiggle && wiggleKey,
        })}
        key={shouldWiggle ? `stage-${wiggleKey}` : `stage-${id}`}
      >
        <div className={styles.hoverRegion}>
          {!creating && (
            <i
              className={cx(styles.icon, "paperflite-drag", styles.drag, {
                [styles.showDrag]: !editing && editMode,
              })}
            />
          )}
          <div
            className={cx(styles.titleGroup, {
              [styles.moveOnShowDrag]: !editing && editMode,
              [styles.disabled]: creating,
              [styles.emptyStage]: !editMode && totalActions === 0,
            })}
          >
            <i
              onClick={handleAccordionClick}
              className={cx(
                styles.icon,
                "paperflite-dropdown",
                styles.collapse,
                {
                  [styles.open]: isStageAccordionOpen,
                  [styles.disabled]: creating || (!editMode && totalActions === 0),
                },
              )}
            />
            {editMode && (editing || creating) ? (
              <input
                value={title}
                className={styles.titleInput}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e)=>{
                  if(e.key === "Enter"){
                    onUpdateStage();
                  }
                }}
                autoFocus
                placeholder={"Name your stage (e.g., Discovery, Proposal, Onboarding)"}
              />
            ) : (
              <div className={styles.titleContainer} onClick={handleTitleClick}>
                <Text
                  className={cx(styles.title, {
                    [styles.completed]: isCompleted,
                  })}
                  text={title}
                  lines={1}
                />
              </div>
            )}
          </div>
        </div>

        {!((creating || editing) && editMode) && (
          <div className={cx(styles.statusContainer, {
            [styles.allowHover]: editMode,
          })}>
            <StatusBadge status={stageStatus} />

            <span
              className={styles.actionsCompleted}
            >{`${completedActions}/${totalActions}`}</span>

            {editMode && <div className={styles.separator}></div>}

            {editMode && <div
              className={cx(styles.deleteContainer, {
                [styles.disabled]: isStageDeleting,
              })}
              onClick={() => clickDeleteStage(id)}
            >
              <i className={cx(styles.deleteIcon, "paperflite-delete")} />
            </div>}
          </div>
        )}

        {((creating || editing) && editMode) && (
          <div className={styles.editActionsContainer}>
            <button className={styles.iconButton} onClick={onUpdateStage}>
              <i className={cx(styles.icon, "paperflite-tick", styles.save, {
                [styles.disabled]: isEditStageSaving || isCreateStageSaving || _.size(title.trim()) === 0,
              })} />
            </button>
            <div className={styles.separator}></div>
            <button className={styles.iconButton} onClick={onCancelUpdateStage}>
              <i
                className={cx(styles.icon, "paperflite-close", styles.cancel, {
                  [styles.disabled]: isEditStageSaving || isCreateStageSaving,
                })}
              />
            </button>
          </div>
        )}
      </div>

      <Collapse
        isOpen={isStageAccordionOpen}
        customStyles={{
          width: "100%",
          height: "100%",
        }}
      >
        <StageActions stageId={id} />
      </Collapse>
    </div>
  );
};

export default Stage;
